import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";

const ReportUserDrillDown = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [unblockingId, setUnblockingId] = useState(null);
    const [moderatingUser, setModeratingUser] = useState(false);
    const [expandedProductId, setExpandedProductId] = useState(null);

    useEffect(() => {
        fetchUserProducts();
    }, [userId]);

    const fetchUserProducts = async () => {
        setLoading(true);
        try {
            const response = await AdminService.getUserProducts(userId);
            if (response.data && response.data.status) {
                setUserData(response.data.data);
            } else {
                toast.error("Failed to fetch reported products");
            }
        } catch (error) {
            console.error("Error fetching reported products:", error);
            toast.error("An error occurred while fetching reported products");
        } finally {
            setLoading(false);
        }
    };

    const handleUnblock = async (productId, productTitle) => {
        if (!window.confirm(`Are you sure you want to unblock "${productTitle || 'Product #' + productId}"? It will become visible in listings again.`)) return;
        
        setUnblockingId(productId);
        try {
            const response = await AdminService.unblockProduct(productId);
            if (response.data && response.data.status) {
                toast.success("Product unblocked successfully!");
                fetchUserProducts();
            } else {
                toast.error(response.data?.message || "Failed to unblock product");
            }
        } catch (error) {
            console.error("Unblock error:", error);
            toast.error("An error occurred");
        } finally {
            setUnblockingId(null);
        }
    };

    const handleUserModeration = async (action, label) => {
        if (!window.confirm(`Are you sure you want to ${label} this user?`)) return;

        setModeratingUser(true);
        try {
            let response;
            switch (action) {
                case 'BLOCK': response = await AdminService.moderatorBlockUser(userId); break;
                case 'UNBLOCK': response = await AdminService.moderatorUnblockUser(userId); break;
                case 'ALERT': response = await AdminService.moderatorSendAlert(userId); break;
                case 'PERMANENT': response = await AdminService.moderatorPermanentlyBlock(userId); break;
                default: return;
            }

            if (response.data && response.data.status) {
                toast.success(response.data.message || `${label} successful`);
                fetchUserProducts();
            } else {
                toast.error(response.data?.message || `Failed to ${label}`);
            }
        } catch (error) {
            console.error(`User moderation error (${action}):`, error);
            toast.error(error.response?.data?.message || "An error occurred");
        } finally {
            setModeratingUser(false);
        }
    };

    const getModerationBadge = (status) => {
        switch (status) {
            case "DISABLED": return { bg: '#fff3e0', color: '#e65100', label: 'Auto-Disabled' };
            case "BLOCKED": return { bg: '#fce4ec', color: '#c62828', label: 'Blocked' };
            case "PERMANENTLY_BLOCKED": return { bg: '#f3e5f5', color: '#6a1b9a', label: 'Permanent Block' };
            case "ACTIVE": return { bg: '#e8f5e9', color: '#2e7d32', label: 'Active' };
            default: return { bg: '#f5f5f5', color: '#757575', label: status || '—' };
        }
    };

    const getRiskInfo = (percentage) => {
        if (percentage >= 50) return { label: 'HIGH RISK', color: '#d32f2f', bg: '#ffebee', icon: 'fa-exclamation-triangle' };
        if (percentage > 20) return { label: 'MODERATE RISK', color: '#f57c00', bg: '#fff3e0', icon: 'fa-warning' };
        return { label: 'LOW RISK', color: '#388e3c', bg: '#e8f5e9', icon: 'fa-check-circle' };
    };

    if (loading) {
        return (
            <div className="admin-container">
                <div className="text-center p-5">
                    <i className="fa fa-spinner fa-spin fa-2x" style={{ color: 'var(--admin-primary)' }}></i>
                    <p style={{ marginTop: '10px', color: '#666' }}>Loading user details...</p>
                </div>
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="admin-container">
                <div className="text-center p-5">
                    <p>User not found.</p>
                    <button className="admin-btn admin-btn-outline" onClick={() => navigate('/admin/reports')}>
                        Back to Reports
                    </button>
                </div>
            </div>
        );
    }

    const { products = [], riskPercentage = 0, userStatus = 'ACTIVE' } = userData;
    const riskInfo = getRiskInfo(riskPercentage);
    const userBadge = getModerationBadge(userStatus);

    return (
        <div className="admin-container">
            {/* Header & Back Action */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => navigate('/admin/reports')}>
                    <i className="fa fa-arrow-left"></i> Back to Reports
                </button>
                <div style={{ 
                    background: riskInfo.bg, color: riskInfo.color, padding: '6px 15px', borderRadius: '20px', 
                    fontSize: '12px', fontWeight: '800', border: `1px solid ${riskInfo.color}33`, display: 'flex', alignItems: 'center', gap: '8px' 
                }}>
                    <i className={`fa ${riskInfo.icon}`}></i>
                    {riskInfo.label} ({riskPercentage.toFixed(1)}%)
                </div>
            </div>

            {/* User Profile & Moderation Card */}
            <div className="admin-card" style={{ marginBottom: '25px', overflow: 'hidden' }}>
                <div className="admin-card-body" style={{ padding: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {/* User Details Section */}
                        <div style={{ flex: '1 1 400px', padding: '25px', borderRight: '1px solid #eee' }}>
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                {userData.userImageUrl ? (
                                    <img
                                        src={userData.userImageUrl}
                                        alt="User"
                                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #f8f9fa' }}
                                    />
                                ) : (
                                    <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #1976d2, #1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '32px' }}>
                                        <i className="fa fa-user"></i>
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                                        <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#222' }}>
                                            {userData.userName}
                                        </h3>
                                        <span style={{ background: userBadge.bg, color: userBadge.color, padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase' }}>
                                            {userBadge.label}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                        <i className="fa fa-envelope" style={{ marginRight: '8px', opacity: 0.6 }}></i>
                                        {userData.userEmail}
                                        <span style={{ margin: '0 12px', color: '#ddd' }}>|</span>
                                        <span style={{ fontWeight: '500' }}>ID: #{userData.userId}</span>
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: '25px', marginTop: '15px' }}>
                                        <div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>{userData.totalProducts}</div>
                                            <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Total Products</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#f44336' }}>{userData.reportedProducts}</div>
                                            <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Reported</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: riskInfo.color }}>{riskPercentage.toFixed(1)}%</div>
                                            <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Risk Factor</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* User Actions Section */}
                        <div style={{ flex: '1 1 300px', padding: '25px', background: '#fafafa', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#999', textTransform: 'uppercase', marginBottom: '15px', letterSpacing: '0.5px' }}>
                                User Moderation Actions
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                {userStatus === 'ACTIVE' ? (
                                    <button 
                                        className="admin-btn" 
                                        onClick={() => handleUserModeration('BLOCK', 'Block User')}
                                        disabled={moderatingUser}
                                        style={{ background: '#ffa000', borderColor: '#ffa000', color: '#fff' }}
                                    >
                                        <i className="fa fa-ban"></i> Block User
                                    </button>
                                ) : (
                                    <button 
                                        className="admin-btn" 
                                        onClick={() => handleUserModeration('UNBLOCK', 'Restore User')}
                                        disabled={moderatingUser || userStatus === 'PERMANENTLY_BLOCKED'}
                                        style={{ background: '#43a047', borderColor: '#43a047', color: '#fff', opacity: userStatus === 'PERMANENTLY_BLOCKED' ? 0.5 : 1 }}
                                    >
                                        <i className="fa fa-undo"></i> Restore User
                                    </button>
                                )}

                                <button 
                                    className="admin-btn admin-btn-outline" 
                                    onClick={() => handleUserModeration('ALERT', 'Send Warning')}
                                    disabled={moderatingUser}
                                >
                                    <i className="fa fa-bell"></i> Send Warning
                                </button>

                                <button 
                                    className="admin-btn" 
                                    onClick={() => handleUserModeration('PERMANENT', 'Permanently Block')}
                                    disabled={moderatingUser || userStatus === 'PERMANENTLY_BLOCKED'}
                                    style={{ background: '#212121', borderColor: '#212121', color: '#fff', gridColumn: 'span 2' }}
                                >
                                    <i className="fa fa-lock"></i> Permanent Block Account
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reported Products Table */}
            <div className="admin-card">
                <div className="admin-card-body" style={{ padding: 0 }}>
                    <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontWeight: '700', color: '#333', fontSize: '16px' }}>
                            <i className="fa fa-list-alt" style={{ marginRight: '8px', color: 'var(--admin-primary)' }}></i>
                            Listing Portfolio & Reports
                        </h4>
                    </div>
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th>Product Details</th>
                                    <th>Price</th>
                                    <th>Status</th>
                                    <th>Reports</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.length > 0 ? (
                                    products.map((product) => {
                                        const modBadge = getModerationBadge(product.moderationStatus);
                                        const isProductBlocked = product.moderationStatus === 'BLOCKED' || product.moderationStatus === 'DISABLED';
                                        const isProductPermanent = product.moderationStatus === 'PERMANENTLY_BLOCKED';
                                        const isExpanded = expandedProductId === product.productId;
                                        
                                        return (
                                            <React.Fragment key={product.productId}>
                                                <tr style={{ cursor: 'pointer', background: isExpanded ? '#f8f9fa' : 'transparent' }} onClick={() => setExpandedProductId(isExpanded ? null : product.productId)}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: '10px', color: '#999' }}></i>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            {product.thumbnailUrl ? (
                                                                <img
                                                                    src={product.thumbnailUrl}
                                                                    alt="Product"
                                                                    style={{ width: '45px', height: '45px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' }}
                                                                />
                                                            ) : (
                                                                <div style={{ width: '45px', height: '45px', borderRadius: '8px', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                                                                    <i className="fa fa-image"></i>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                                                                    {product.title}
                                                                </div>
                                                                <div style={{ fontSize: '11px', color: '#999' }}>ID: #{product.productId}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ fontWeight: '700', color: '#2C3E50' }}>
                                                        ${product.price?.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            background: modBadge.bg, color: modBadge.color,
                                                            padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700'
                                                        }}>
                                                            {modBadge.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {product.reportCount > 0 ? (
                                                            <span style={{
                                                                background: product.reportCount >= 3 ? '#ffebee' : '#fff3e0',
                                                                color: product.reportCount >= 3 ? '#c62828' : '#e65100',
                                                                padding: '4px 12px', borderRadius: '12px', fontWeight: '700', fontSize: '12px'
                                                            }}>
                                                                <i className="fa fa-flag" style={{ marginRight: '5px' }}></i>
                                                                {product.reportCount}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#ccc', fontSize: '12px' }}>Clean</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                            {isProductBlocked && !isProductPermanent && (
                                                                <button
                                                                    className="admin-btn admin-btn-sm"
                                                                    onClick={(e) => { e.stopPropagation(); handleUnblock(product.productId, product.title); }}
                                                                    disabled={unblockingId === product.productId}
                                                                    style={{ background: '#2e7d32', color: '#fff' }}
                                                                >
                                                                    {unblockingId === product.productId ? <i className="fa fa-spinner fa-spin"></i> : 'Unblock'}
                                                                </button>
                                                            )}
                                                            {!isProductBlocked && !isProductPermanent && (
                                                                <i className="fa fa-check-circle" style={{ color: '#4caf50' }} title="Product is visible"></i>
                                                            )}
                                                            {isProductPermanent && (
                                                                <i className="fa fa-lock" style={{ color: '#999' }} title="Permanently Locked"></i>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                
                                                {/* Expanded Report Details */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="6" style={{ padding: '0', background: '#fdfdfd' }}>
                                                            <div style={{ padding: '20px 20px 25px 52px', borderBottom: '1px solid #eee' }}>
                                                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#666', marginBottom: '15px' }}>
                                                                    REPORTS FOR THIS PRODUCT
                                                                </div>
                                                                
                                                                {product.reportEntries && product.reportEntries.length > 0 ? (
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                                        {product.reportEntries.map((entry) => (
                                                                            <div key={entry.id} style={{ 
                                                                                padding: '12px 15px', background: '#fff', borderRadius: '8px', 
                                                                                border: '1px solid #eee', position: 'relative' 
                                                                            }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                                                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#333' }}>
                                                                                        {entry.reporterName} 
                                                                                        <span style={{ fontWeight: '400', color: '#999', fontSize: '11px', marginLeft: '8px' }}>(Reporter ID: #{entry.reporterId})</span>
                                                                                    </div>
                                                                                    <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '500' }}>
                                                                                        {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                    </div>
                                                                                </div>
                                                                                <div style={{ marginBottom: '5px' }}>
                                                                                    <span style={{ background: '#f5f5f5', color: '#666', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase' }}>
                                                                                        {entry.reasonCode}
                                                                                    </span>
                                                                                </div>
                                                                                {entry.comment && (
                                                                                    <div style={{ fontSize: '13px', color: '#555', fontStyle: 'italic', lineHeight: '1.4', background: '#fcfcfc', padding: '8px', borderRadius: '4px', marginTop: '5px' }}>
                                                                                        "{entry.comment}"
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                                                                        No detailed reports available for this item.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center p-5">
                                            <p style={{ opacity: 0.5 }}>This user has no products at the moment.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportUserDrillDown;
